import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function SettingsPage() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_new_password: "" });
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const save = async (e) => {
    e.preventDefault();

    if (form.new_password !== form.confirm_new_password) {
      toast.error("New passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated.");
      setForm({ current_password: "", new_password: "", confirm_new_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <form onSubmit={save} className="sqit-card p-8 space-y-5" data-testid="settings-password-form">
        <div>
          <h3 className="font-display text-lg font-semibold text-gray-900">Change password</h3>
          <p className="text-xs text-gray-500 mt-1">Use a strong password of at least 8 characters.</p>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-gray-700">
            Current password
          </label>

          <div className="relative">
            <input
              data-testid="settings-current-password"
              type={showCurrentPassword ? "text" : "password"}
              required
              value={form.current_password}
              onChange={(e) =>
                setForm({ ...form, current_password: e.target.value })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-[#0B6936] focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20"
            />

            <button
              type="button"
              onClick={() => setShowCurrentPassword((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-[#0B6936]"
            >
              {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-gray-700">
            New password
          </label>

          <div className="relative">
            <input
              data-testid="settings-new-password"
              type={showNewPassword ? "text" : "password"}
              required
              minLength={8}
              value={form.new_password}
              onChange={(e) =>
                setForm({ ...form, new_password: e.target.value })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-[#0B6936] focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20"
            />

            <button
              type="button"
              onClick={() => setShowNewPassword((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-[#0B6936]"
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-gray-700">
            Confirm new password
          </label>

          <div className="relative">
            <input
              data-testid="settings-confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              required
              minLength={8}
              value={form.confirm_new_password}
              onChange={(e) =>
                setForm({ ...form, confirm_new_password: e.target.value })
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-[#0B6936] focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20"
            />

            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-[#0B6936]"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <button
          disabled={saving}
          data-testid="settings-save"
          className="rounded-md bg-[#0B6936] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#07522f] disabled:opacity-60"
        >
          {saving ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
